import dayjs, { Dayjs } from 'dayjs';
import { resolveAssignmentRosterContext, resolveOperationRosterShift } from '../SolverShared/manualOverride';

export type AttendanceKind = 'WORK' | 'REST' | 'PAID_LEAVE' | 'UNPAID_LEAVE';

export interface V4ShiftDefinition {
    shift_id: number;
    shift_name: string;
    shift_code: string;
    start_time: string;
    end_time: string;
    nominal_hours: number;
    credited_hours: number;
    attendance_kind: AttendanceKind;
    category: string;
    is_night_shift: boolean;
    is_system_managed: boolean;
    is_active: boolean;
}

export interface V4ShiftAssignment extends V4ShiftDefinition {
    employee_id: number;
    employee_name: string;
    employee_code: string;
    date: string;
    plan_type: AttendanceKind;
    is_locked?: boolean;
    lock_reason?: string | null;
}

export interface V4QualificationRequirement {
    qualification_id: number;
    qualification_name: string;
    required_level: number;
    is_mandatory: boolean;
}

export interface V4OperationPosition {
    position_number: number;
    status: 'ASSIGNED' | 'UNASSIGNED';
    employee: { id: number; name: string; code: string } | null;
    qualification_requirements?: V4QualificationRequirement[];
    eligible_employee_ids?: number[] | null;
    roster_date?: string | null;
    shift_date?: string | null;
    shift_id?: number | null;
    shift_plan_id?: number | null;
    assignment_start?: string | null;
    assignment_end?: string | null;
    is_locked?: boolean;
    locked_employee_id?: number | null;
}

export interface V4Operation {
    operation_plan_id: number;
    batch_code: string;
    operation_name: string;
    planned_start: string;
    planned_end: string;
    required_people?: number;
    share_group_ids?: string | null;
    share_group_name?: string | null;
    status: 'COMPLETE' | 'PARTIAL' | 'UNASSIGNED';
    positions: V4OperationPosition[];
    stage_name?: string | null;
    task_type?: 'FLEXIBLE' | 'AD_HOC' | 'RECURRING' | null;
    duration_minutes?: number;
    window_start?: string | null;
    window_end?: string | null;
}

export interface V4EmployeeMeta {
    code: string;
    name: string;
    department: string | null;
    qualifications: { qualification_id: number; level: number }[];
    unavailable_periods?: {
        start: string;
        end: string;
        label?: string;
        reason_code?: string;
    }[];
    eligible_shift_ids?: number[];
}

export interface V4PersistedAssignment {
    operation_plan_id: number;
    position_number: number;
    employee_id: number;
    employee_name?: string;
    employee_code?: string;
    operation_name?: string;
    batch_code?: string;
    planned_start?: string;
    planned_end?: string;
    date?: string | null;
    shift_date?: string | null;
    shift_id?: number | null;
    shift_plan_id?: number | null;
}

export interface V4ResultData {
    contract_version?: number;
    metrics: {
        completion_rate: number;
        coverage_rate: number;
        satisfaction: number;
        solve_time: number;
        special_shift_requirement_count?: number;
        special_shift_occurrence_count?: number;
        special_shift_required_headcount_total?: number;
        special_shift_assigned_headcount_total?: number;
        special_shift_shortage_total?: number;
        special_shift_unmet_occurrence_count?: number;
        special_shift_partial_occurrence_count?: number;
    };
    details: {
        total_positions: number;
        assigned_positions: number;
        total_operations: number;
        covered_operations: number;
    };
    assignments: V4PersistedAssignment[];
    shift_assignments: V4ShiftAssignment[];
    shift_definitions: V4ShiftDefinition[];
    operations: V4Operation[];
    employee_meta: Record<number, V4EmployeeMeta>;
    calendar_days: { date: string; is_workday: boolean }[];
    standard_hours?: number;
    run_status?: string;
    constraint_config?: {
        enable_share_group?: boolean;
        enable_unique_employee?: boolean;
    };
    draft?: { exists: boolean; version: number; saved_at: string | null };
    precheck_results?: {
        status: 'PASS' | 'WARNING' | 'ERROR';
        checks: {
            name: string;
            status: 'PASS' | 'WARNING' | 'ERROR';
            message: string;
            details?: unknown[];
        }[];
    } | null;
    special_shift_assignments?: Array<{
        occurrence_id: number;
        employee_id: number;
        date: string;
        shift_id: number;
    }>;
    special_shift_shortages?: Array<{
        occurrence_id: number;
        shortage_people: number;
    }>;
}

export interface V4AssignmentContext {
    date: string;
    shift_id: number;
    planned_start: string;
    planned_end: string;
}

export type V4ResultCommand =
    | {
        type: 'CHANGE_SHIFT';
        employeeId: number;
        date: string;
        shiftId: number;
    }
    | {
        type: 'ASSIGN_EMPLOYEE';
        operationId: number;
        positionNumber: number;
        employee: { id: number; name: string; code: string };
        context: V4AssignmentContext;
    }
    | {
        type: 'UNASSIGN_EMPLOYEE';
        operationId: number;
        positionNumber: number;
    }
    | {
        type: 'ASSIGN_AND_CHANGE_SHIFT';
        operationId: number;
        positionNumber: number;
        employee: { id: number; name: string; code: string };
        context: V4AssignmentContext;
        shiftId: number;
    };

export interface V4HistoryEntry {
    id: number;
    label: string;
    before: V4ResultData;
    after: V4ResultData;
}

export interface V4ResultEditorState {
    baseSnapshot: V4ResultData | null;
    workingResult: V4ResultData | null;
    past: V4HistoryEntry[];
    future: V4HistoryEntry[];
}

export type V4ResultEditorAction =
    | { type: 'LOAD'; result: V4ResultData }
    | { type: 'CLEAR' }
    | { type: 'EXECUTE'; command: V4ResultCommand; label: string }
    | { type: 'UNDO' }
    | { type: 'REDO' }
    | { type: 'RESET' };

export const initialV4ResultEditorState: V4ResultEditorState = {
    baseSnapshot: null,
    workingResult: null,
    past: [],
    future: [],
};

const normalizeAttendanceKind = (value: unknown, hours: number): AttendanceKind => {
    const kind = String(value || '').toUpperCase();
    if (kind === 'WORK' || kind === 'REST' || kind === 'PAID_LEAVE' || kind === 'UNPAID_LEAVE') {
        return kind;
    }
    return hours > 0.01 ? 'WORK' : 'REST';
};

export const normalizeV4Result = (source: any): V4ResultData => {
    const shiftDefinitions: V4ShiftDefinition[] = (
        Array.isArray(source.shift_definitions) && source.shift_definitions.length > 0
            ? source.shift_definitions
            : Array.from(
                new Map(
                    (source.shift_assignments || []).map((shift: any) => [Number(shift.shift_id), shift]),
                ).values(),
            )
    ).map((shift: any) => {
        const nominalHours = Number(shift.nominal_hours ?? shift.shift_nominal_hours ?? 0);
        const attendanceKind = normalizeAttendanceKind(
            shift.attendance_kind ?? shift.plan_type,
            nominalHours,
        );
        return {
            shift_id: Number(shift.shift_id ?? shift.id),
            shift_name: String(shift.shift_name || ''),
            shift_code: String(shift.shift_code || ''),
            start_time: String(shift.start_time || '00:00').slice(0, 8),
            end_time: String(shift.end_time || '00:00').slice(0, 8),
            nominal_hours: nominalHours,
            credited_hours: Number(shift.credited_hours ?? shift.plan_hours ?? nominalHours),
            attendance_kind: attendanceKind,
            category: String(shift.category || 'BASE'),
            is_night_shift: Boolean(shift.is_night_shift),
            is_system_managed: Boolean(shift.is_system_managed),
            is_active: shift.is_active !== false,
        };
    });
    const definitionById = new Map(shiftDefinitions.map((shift) => [shift.shift_id, shift]));
    const shiftAssignments: V4ShiftAssignment[] = (source.shift_assignments || []).map((shift: any) => {
        const definition = definitionById.get(Number(shift.shift_id));
        const nominalHours = Number(shift.nominal_hours ?? shift.shift_nominal_hours ?? definition?.nominal_hours ?? 0);
        const attendanceKind = normalizeAttendanceKind(
            shift.attendance_kind ?? shift.plan_type ?? definition?.attendance_kind,
            nominalHours,
        );
        return {
            ...(definition || {
                shift_id: Number(shift.shift_id),
                shift_name: String(shift.shift_name || ''),
                shift_code: String(shift.shift_code || ''),
                start_time: String(shift.start_time || '00:00').slice(0, 8),
                end_time: String(shift.end_time || '00:00').slice(0, 8),
                nominal_hours: nominalHours,
                credited_hours: Number(shift.credited_hours ?? shift.plan_hours ?? nominalHours),
                attendance_kind: attendanceKind,
                category: String(shift.category || 'BASE'),
                is_night_shift: Boolean(shift.is_night_shift),
                is_system_managed: Boolean(shift.is_system_managed),
                is_active: true,
            }),
            employee_id: Number(shift.employee_id),
            employee_name: String(shift.employee_name || `员工 ${shift.employee_id}`),
            employee_code: String(shift.employee_code || ''),
            date: String(shift.date).slice(0, 10),
            plan_type: attendanceKind,
            is_locked: Boolean(shift.is_locked),
            lock_reason: shift.lock_reason == null ? null : String(shift.lock_reason),
        };
    });
    const operations: V4Operation[] = (source.operations || []).map((operation: any) => ({
        ...operation,
        operation_plan_id: Number(operation.operation_plan_id),
        batch_code: String(operation.batch_code || ''),
        operation_name: String(operation.operation_name || ''),
        planned_start: String(operation.planned_start || ''),
        planned_end: String(operation.planned_end || ''),
        positions: (operation.positions || []).map((position: any) => ({
            ...position,
            position_number: Number(position.position_number),
            status: position.status === 'ASSIGNED' && position.employee ? 'ASSIGNED' : 'UNASSIGNED',
            employee: position.employee
                ? {
                    id: Number(position.employee.id),
                    name: String(position.employee.name || ''),
                    code: String(position.employee.code || ''),
                }
                : null,
            roster_date: position.roster_date == null ? null : String(position.roster_date).slice(0, 10),
            shift_date: position.shift_date == null ? null : String(position.shift_date).slice(0, 10),
            shift_id: position.shift_id == null ? null : Number(position.shift_id),
            shift_plan_id: position.shift_plan_id == null ? null : Number(position.shift_plan_id),
            assignment_start: position.assignment_start == null ? null : String(position.assignment_start),
            assignment_end: position.assignment_end == null ? null : String(position.assignment_end),
            is_locked: Boolean(position.is_locked),
            locked_employee_id: position.locked_employee_id == null ? null : Number(position.locked_employee_id),
        })),
    }));
    return {
        ...source,
        contract_version: Number(source.contract_version || 1),
        assignments: source.assignments || [],
        shift_assignments: shiftAssignments,
        shift_definitions: shiftDefinitions,
        operations,
        employee_meta: source.employee_meta || {},
        calendar_days: source.calendar_days || [],
        special_shift_assignments: source.special_shift_assignments || [],
        special_shift_shortages: source.special_shift_shortages || [],
    };
};

const updateOperationPosition = (
    result: V4ResultData,
    operationId: number,
    positionNumber: number,
    updater: (position: V4OperationPosition) => V4OperationPosition,
): V4ResultData => ({
    ...result,
    operations: result.operations.map((operation) => {
        if (operation.operation_plan_id !== operationId) return operation;
        const positions = operation.positions.map((position) => (
            position.position_number === positionNumber ? updater(position) : position
        ));
        const assigned = positions.filter((position) => position.status === 'ASSIGNED' && position.employee).length;
        return {
            ...operation,
            positions,
            status: assigned === positions.length ? 'COMPLETE' : assigned > 0 ? 'PARTIAL' : 'UNASSIGNED',
        };
    }),
});

const changeShift = (
    result: V4ResultData,
    employeeId: number,
    date: string,
    shiftId: number,
): V4ResultData => {
    const definition = result.shift_definitions.find((shift) => shift.shift_id === shiftId);
    if (!definition) return result;
    const currentAssignment = result.shift_assignments.find((assignment) => (
        assignment.employee_id === employeeId && assignment.date === date
    ));
    if (!currentAssignment || currentAssignment.is_locked) return result;
    return {
        ...result,
        shift_assignments: result.shift_assignments.map((assignment) => {
            if (assignment.employee_id !== employeeId || assignment.date !== date) return assignment;
            if (assignment.is_locked) return assignment;
            return {
                ...definition,
                employee_id: assignment.employee_id,
                employee_name: assignment.employee_name,
                employee_code: assignment.employee_code,
                date: assignment.date,
                plan_type: definition.attendance_kind,
                is_locked: assignment.is_locked,
                lock_reason: assignment.lock_reason,
            };
        }),
        operations: result.operations.map((operation) => ({
            ...operation,
            positions: operation.positions.map((position) => {
                if (position.employee?.id !== employeeId || position.status !== 'ASSIGNED') {
                    return position;
                }
                if (
                    position.shift_date
                    && position.shift_plan_id
                    && position.shift_date !== position.roster_date
                ) {
                    // The prior-day shift is frozen and represented only by a
                    // persisted plan reference. Editing the mutable solve-day
                    // roster must not corrupt that boundary assignment.
                    return position;
                }
                const legacyContext = !position.roster_date
                    ? resolveAssignmentRosterContext(
                        employeeId,
                        operation.operation_plan_id,
                        position.position_number,
                        operation,
                        result.shift_assignments,
                        result.assignments,
                    )
                    : null;
                if ((position.roster_date || legacyContext?.date) !== date) return position;
                return {
                    ...position,
                    roster_date: date,
                    shift_id: shiftId,
                    assignment_start: position.assignment_start || operation.planned_start,
                    assignment_end: position.assignment_end || operation.planned_end,
                };
            }),
        })),
    };
};

export const applyV4ResultCommand = (
    result: V4ResultData,
    command: V4ResultCommand,
): V4ResultData => {
    if (command.type === 'CHANGE_SHIFT') {
        return changeShift(result, command.employeeId, command.date, command.shiftId);
    }
    if (command.type === 'UNASSIGN_EMPLOYEE') {
        return updateOperationPosition(
            result,
            command.operationId,
            command.positionNumber,
            (position) => position.is_locked ? position : {
                ...position,
                status: 'UNASSIGNED',
                employee: null,
                roster_date: null,
                shift_date: null,
                shift_id: null,
                shift_plan_id: null,
                assignment_start: null,
                assignment_end: null,
            },
        );
    }
    const next = command.type === 'ASSIGN_AND_CHANGE_SHIFT'
        ? changeShift(result, command.employee.id, command.context.date, command.shiftId)
        : result;
    return updateOperationPosition(
        next,
        command.operationId,
        command.positionNumber,
        (position) => position.is_locked ? position : {
            ...position,
            status: 'ASSIGNED',
            employee: command.employee,
            roster_date: command.context.date,
            shift_date: null,
            shift_id: command.context.shift_id,
            shift_plan_id: null,
            assignment_start: command.context.planned_start,
            assignment_end: command.context.planned_end,
        },
    );
};

export const v4ResultEditorReducer = (
    state: V4ResultEditorState,
    action: V4ResultEditorAction,
): V4ResultEditorState => {
    if (action.type === 'CLEAR') {
        return initialV4ResultEditorState;
    }
    if (action.type === 'LOAD') {
        return {
            baseSnapshot: action.result,
            workingResult: action.result,
            past: [],
            future: [],
        };
    }
    if (!state.workingResult) return state;
    if (action.type === 'EXECUTE') {
        const after = applyV4ResultCommand(state.workingResult, action.command);
        if (after === state.workingResult) return state;
        return {
            ...state,
            workingResult: after,
            past: [
                ...state.past,
                {
                    id: Date.now() + state.past.length,
                    label: action.label,
                    before: state.workingResult,
                    after,
                },
            ],
            future: [],
        };
    }
    if (action.type === 'UNDO') {
        const entry = state.past[state.past.length - 1];
        if (!entry) return state;
        return {
            ...state,
            workingResult: entry.before,
            past: state.past.slice(0, -1),
            future: [entry, ...state.future],
        };
    }
    if (action.type === 'REDO') {
        const [entry, ...remaining] = state.future;
        if (!entry) return state;
        return {
            ...state,
            workingResult: entry.after,
            past: [...state.past, entry],
            future: remaining,
        };
    }
    if (action.type === 'RESET' && state.baseSnapshot) {
        return {
            ...state,
            workingResult: state.baseSnapshot,
            past: [],
            future: [],
        };
    }
    return state;
};

const timePart = (value: string): { clock: string; dayOffset: number } => {
    const match = value.match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return { clock: '00:00:00', dayOffset: 0 };
    const hour = Number(match[1]);
    return {
        clock: `${String(hour % 24).padStart(2, '0')}:${match[2]}:${match[3] || '00'}`,
        dayOffset: Math.floor(hour / 24),
    };
};

export const buildShiftWindow = (
    date: string,
    shift: Pick<V4ShiftDefinition, 'start_time' | 'end_time'>,
): { start: Dayjs; end: Dayjs } => {
    const startTime = timePart(shift.start_time);
    const endTime = timePart(shift.end_time);
    const start = dayjs(`${date}T${startTime.clock}`).add(startTime.dayOffset, 'day');
    let end = dayjs(`${date}T${endTime.clock}`).add(endTime.dayOffset, 'day');
    if (endTime.dayOffset === 0 && !end.isAfter(start)) end = end.add(1, 'day');
    return { start, end };
};

type OperationExecutionSource = {
    planned_start: string;
    planned_end: string;
    positions?: Array<{
        status: 'ASSIGNED' | 'UNASSIGNED';
        assignment_start?: string | null;
        assignment_end?: string | null;
    }>;
};

export const resolveAssignedOperationExecutionWindow = (
    operation: OperationExecutionSource,
): { start: string; end: string } | null => {
    const position = operation.positions?.find((candidate) => (
        candidate.status === 'ASSIGNED'
        && Boolean(candidate.assignment_start)
        && Boolean(candidate.assignment_end)
        && dayjs(candidate.assignment_start).isValid()
        && dayjs(candidate.assignment_end).isValid()
    ));
    return position?.assignment_start && position.assignment_end
        ? { start: position.assignment_start, end: position.assignment_end }
        : null;
};

export const resolveOperationExecutionWindow = (
    operation: OperationExecutionSource,
): { start: string; end: string } => (
    resolveAssignedOperationExecutionWindow(operation) || {
        start: operation.planned_start,
        end: operation.planned_end,
    }
);

export const buildExecutionWindowContext = (
    date: string,
    shift: V4ShiftDefinition,
    execution: { start: string; end: string },
): V4AssignmentContext | null => {
    if (shift.attendance_kind !== 'WORK') return null;
    const start = dayjs(execution.start);
    const end = dayjs(execution.end);
    const window = buildShiftWindow(date, shift);
    if (
        !start.isValid()
        || !end.isValid()
        || !end.isAfter(start)
        || start.isBefore(window.start)
        || end.isAfter(window.end)
    ) {
        return null;
    }
    return {
        date,
        shift_id: shift.shift_id,
        planned_start: execution.start,
        planned_end: execution.end,
    };
};

export const resolveCandidateContext = (
    result: V4ResultData,
    employeeId: number,
    operation: V4Operation,
): V4AssignmentContext | null => {
    const shift = resolveOperationRosterShift(
        employeeId,
        operation,
        result.shift_assignments,
    ) as V4ShiftAssignment | null;
    if (!shift) return null;
    return {
        date: shift.date,
        shift_id: shift.shift_id,
        planned_start: operation.planned_start,
        planned_end: operation.planned_end,
    };
};

export const buildFlexibleTaskContext = (
    operation: V4Operation,
    date: string,
    shift: V4ShiftDefinition,
): V4AssignmentContext | null => {
    if (operation.task_type !== 'FLEXIBLE') return null;
    const durationMinutes = Number(operation.duration_minutes || 0);
    if (durationMinutes <= 0 || shift.attendance_kind !== 'WORK') return null;
    const window = buildShiftWindow(date, shift);
    const allowedStart = operation.window_start ? dayjs(operation.window_start) : null;
    const taskStart = allowedStart?.isValid() && allowedStart.isAfter(window.start)
        ? allowedStart
        : window.start;
    const taskEnd = taskStart.add(durationMinutes, 'minute');
    const allowedEnd = operation.window_end ? dayjs(operation.window_end) : null;
    if (allowedEnd?.isValid() && taskEnd.isAfter(allowedEnd)) return null;
    if (taskEnd.isAfter(window.end)) return null;
    return {
        date,
        shift_id: shift.shift_id,
        planned_start: taskStart.format('YYYY-MM-DDTHH:mm:ss'),
        planned_end: taskEnd.format('YYYY-MM-DDTHH:mm:ss'),
    };
};

export interface V4ManualOverride {
    assignments: Array<{
        operation_id: number;
        position_number: number;
        employee_id: number;
        is_standalone: boolean;
        date: string;
        shift_date?: string | null;
        shift_id: number;
        shift_plan_id?: number | null;
        planned_start: string;
        planned_end: string;
    }>;
    shift_schedule: Array<{
        employee_id: number;
        date: string;
        shift_id: number;
    }>;
}

export interface V4ValidationIssue {
    code: string;
    severity: 'ERROR' | 'WARNING';
    message: string;
    operationId?: number;
    positionNumber?: number;
    employeeId?: number;
    date?: string;
}

type V4ShareGroupOperation = Pick<V4Operation, 'operation_plan_id' | 'share_group_ids'>;

const parseV4ShareGroupIds = (value: unknown): string[] => {
    const rawValues = Array.isArray(value) ? value : String(value ?? '').split(',');
    return Array.from(new Set(
        rawValues
            .map((item) => String(item).trim())
            .filter(Boolean),
    ));
};

/**
 * Mirrors UniqueEmployeeConstraint._build_op_to_group_uf in solver_v4.
 * Share groups can overlap through a pivot operation, so a direct group-id
 * intersection is insufficient (A↔B and B↔C means A/B/C share one component).
 */
export const buildV4ShareGroupComponentMap = (
    operations: V4ShareGroupOperation[],
): Map<number, number> => {
    const parent = new Map<number, number>();
    const groupAnchor = new Map<string, number>();

    const find = (operationId: number): number => {
        if (!parent.has(operationId)) parent.set(operationId, operationId);
        let root = operationId;
        while (parent.get(root) !== root) {
            root = parent.get(root)!;
        }
        let current = operationId;
        while (parent.get(current) !== root) {
            const next = parent.get(current)!;
            parent.set(current, root);
            current = next;
        }
        return root;
    };

    const union = (firstId: number, secondId: number) => {
        const firstRoot = find(firstId);
        const secondRoot = find(secondId);
        if (firstRoot !== secondRoot) parent.set(firstRoot, secondRoot);
    };

    operations.forEach((operation) => {
        parseV4ShareGroupIds(operation.share_group_ids).forEach((groupId) => {
            const anchor = groupAnchor.get(groupId);
            if (anchor == null) {
                groupAnchor.set(groupId, operation.operation_plan_id);
                find(operation.operation_plan_id);
            } else {
                union(anchor, operation.operation_plan_id);
            }
        });
    });

    const componentByOperation = new Map<number, number>();
    parent.forEach((_, operationId) => {
        componentByOperation.set(operationId, find(operationId));
    });
    return componentByOperation;
};

export const isV4EmployeeOverlapAllowed = (
    first: Pick<V4Operation, 'operation_plan_id' | 'task_type'>,
    second: Pick<V4Operation, 'operation_plan_id' | 'task_type'>,
    componentByOperation: ReadonlyMap<number, number>,
    enableUniqueEmployee = true,
): boolean => {
    if (!enableUniqueEmployee) return true;
    if (first.task_type === 'FLEXIBLE' || second.task_type === 'FLEXIBLE') return true;
    if (first.operation_plan_id === second.operation_plan_id) return false;
    const firstComponent = componentByOperation.get(first.operation_plan_id);
    const secondComponent = componentByOperation.get(second.operation_plan_id);
    return firstComponent != null
        && secondComponent != null
        && firstComponent === secondComponent;
};

export const calculateV4IntervalUnionHours = (
    intervals: Array<{ start: string | Dayjs; end: string | Dayjs }>,
): number => {
    const normalized = intervals
        .map((interval) => ({
            start: dayjs(interval.start),
            end: dayjs(interval.end),
        }))
        .filter((interval) => interval.start.isValid()
            && interval.end.isValid()
            && interval.start.isBefore(interval.end))
        .sort((first, second) => first.start.valueOf() - second.start.valueOf());

    if (normalized.length === 0) return 0;
    let totalMinutes = 0;
    let currentStart = normalized[0].start;
    let currentEnd = normalized[0].end;
    normalized.slice(1).forEach((interval) => {
        if (!interval.start.isAfter(currentEnd)) {
            if (interval.end.isAfter(currentEnd)) currentEnd = interval.end;
            return;
        }
        totalMinutes += currentEnd.diff(currentStart, 'minute');
        currentStart = interval.start;
        currentEnd = interval.end;
    });
    totalMinutes += currentEnd.diff(currentStart, 'minute');
    return Math.max(0, totalMinutes) / 60;
};

export const validateV4WorkingResult = (result: V4ResultData): V4ValidationIssue[] => {
    const issues: V4ValidationIssue[] = [];
    const shareGroupComponentByOperation = buildV4ShareGroupComponentMap(result.operations);
    const enableUniqueEmployee = result.constraint_config?.enable_unique_employee !== false;
    const workShiftsByEmployee = new Map<number, Array<{
        shift: V4ShiftAssignment;
        start: Dayjs;
        end: Dayjs;
    }>>();
    result.shift_assignments.forEach((shift) => {
        if (shift.attendance_kind !== 'WORK') return;
        const window = buildShiftWindow(shift.date, shift);
        const existing = workShiftsByEmployee.get(shift.employee_id) || [];
        const overlap = existing.find((other) => (
            other.start.isBefore(window.end) && window.start.isBefore(other.end)
        ));
        if (overlap) {
            issues.push({
                code: 'SHIFT_OVERLAP',
                severity: 'ERROR',
                message: `${shift.employee_name}的${overlap.shift.date}与${shift.date}班次时间重叠`,
                employeeId: shift.employee_id,
                date: shift.date,
            });
        }
        existing.push({ shift, ...window });
        workShiftsByEmployee.set(shift.employee_id, existing);
    });
    const assignmentsByEmployee = new Map<number, Array<{
        operation: V4Operation;
        position: V4OperationPosition;
        start: Dayjs;
        end: Dayjs;
    }>>();
    result.operations.forEach((operation) => {
        const flexiblePlacement = operation.task_type === 'FLEXIBLE'
            ? resolveAssignedOperationExecutionWindow(operation)
            : null;
        operation.positions.forEach((position) => {
            if (position.status !== 'ASSIGNED' || !position.employee) return;
            const context = position.roster_date && position.shift_id
                && position.assignment_start && position.assignment_end
                ? {
                    date: position.roster_date,
                    shift_id: position.shift_id,
                    planned_start: position.assignment_start,
                    planned_end: position.assignment_end,
                }
                : (() => {
                    const legacy = resolveAssignmentRosterContext(
                        position.employee!.id,
                        operation.operation_plan_id,
                        position.position_number,
                        operation,
                        result.shift_assignments,
                        result.assignments,
                    );
                    return legacy ? {
                        date: legacy.date,
                        shift_id: legacy.shift_id,
                        planned_start: operation.planned_start,
                        planned_end: operation.planned_end,
                    } : null;
                })();
            if (!context) {
                issues.push({
                    code: 'MISSING_ROSTER_CONTEXT',
                    severity: 'ERROR',
                    message: `${position.employee.name} / ${operation.operation_name} 岗位${position.position_number}缺少有效班次上下文`,
                    operationId: operation.operation_plan_id,
                    positionNumber: position.position_number,
                    employeeId: position.employee.id,
                });
                return;
            }
            const isPreservedBoundary = Boolean(
                position.shift_date
                && position.shift_plan_id
                && position.shift_date !== context.date
            );
            const shiftDate = isPreservedBoundary
                ? String(position.shift_date)
                : context.date;
            const roster = isPreservedBoundary
                ? result.shift_definitions.find((shift) => shift.shift_id === context.shift_id)
                : result.shift_assignments.find((shift) => (
                    shift.employee_id === position.employee!.id
                    && shift.date === context.date
                    && shift.shift_id === context.shift_id
                ));
            const start = dayjs(context.planned_start);
            const end = dayjs(context.planned_end);
            if (operation.task_type === 'FLEXIBLE' && start.isValid() && end.isValid()) {
                if (
                    flexiblePlacement
                    && (
                        start.valueOf() !== dayjs(flexiblePlacement.start).valueOf()
                        || end.valueOf() !== dayjs(flexiblePlacement.end).valueOf()
                    )
                ) {
                    issues.push({
                        code: 'TASK_PLACEMENT_MISMATCH',
                        severity: 'ERROR',
                        message: `${operation.operation_name}的所有岗位必须使用同一执行时段`,
                        operationId: operation.operation_plan_id,
                        positionNumber: position.position_number,
                        employeeId: position.employee.id,
                    });
                }
                const rosterDate = dayjs(context.date).startOf('day');
                const allowedStart = operation.window_start
                    ? dayjs(operation.window_start).startOf('day')
                    : null;
                const allowedEnd = operation.window_end
                    ? dayjs(operation.window_end).startOf('day')
                    : null;
                if (
                    !rosterDate.isValid()
                    || (allowedStart?.isValid() && rosterDate.isBefore(allowedStart))
                    || (allowedEnd?.isValid() && rosterDate.isAfter(allowedEnd))
                ) {
                    issues.push({
                        code: 'TASK_WINDOW_VIOLATION',
                        severity: 'ERROR',
                        message: `${operation.operation_name}的排班日期超出允许日期范围`,
                        operationId: operation.operation_plan_id,
                        positionNumber: position.position_number,
                        employeeId: position.employee.id,
                    });
                }
            }
            if (!roster || roster.attendance_kind !== 'WORK') {
                issues.push({
                    code: 'NON_WORK_SHIFT',
                    severity: 'ERROR',
                    message: `${position.employee.name}在${shiftDate}没有可执行该操作的工作班次`,
                    operationId: operation.operation_plan_id,
                    positionNumber: position.position_number,
                    employeeId: position.employee.id,
                    date: shiftDate,
                });
                return;
            }
            if (operation.task_type === 'FLEXIBLE') {
                const requiredMinutes = Number(operation.duration_minutes || 0);
                const availableMinutes = Math.round(Number(roster.nominal_hours || 0) * 60);
                if (requiredMinutes > 0 && availableMinutes > 0 && requiredMinutes > availableMinutes) {
                    issues.push({
                        code: 'TASK_DURATION_EXCEEDS_SHIFT',
                        severity: 'ERROR',
                        message: `${operation.operation_name}所需${requiredMinutes}分钟，超过${roster.shift_name}有效工时${availableMinutes}分钟`,
                        operationId: operation.operation_plan_id,
                        positionNumber: position.position_number,
                        employeeId: position.employee.id,
                    });
                }
            }
            const shiftWindow = buildShiftWindow(shiftDate, roster);
            if (!start.isValid() || !end.isValid() || start.isBefore(shiftWindow.start) || end.isAfter(shiftWindow.end)) {
                issues.push({
                    code: 'SHIFT_NOT_COVERING_OPERATION',
                    severity: 'ERROR',
                    message: `${position.employee.name}的${roster.shift_name}不能覆盖${operation.operation_name}`,
                    operationId: operation.operation_plan_id,
                    positionNumber: position.position_number,
                    employeeId: position.employee.id,
                    date: shiftDate,
                });
            }
            const meta = result.employee_meta[position.employee.id];
            const mandatory = position.qualification_requirements?.filter((requirement) => requirement.is_mandatory) || [];
            const qualificationMissing = mandatory.some((requirement) => (
                !(meta?.qualifications || []).some((qualification) => (
                    qualification.qualification_id === requirement.qualification_id
                    && qualification.level >= requirement.required_level
                ))
            ));
            if (qualificationMissing) {
                issues.push({
                    code: 'QUALIFICATION_MISSING',
                    severity: 'ERROR',
                    message: `${position.employee.name}不满足${operation.operation_name}岗位${position.position_number}的强制资质`,
                    operationId: operation.operation_plan_id,
                    positionNumber: position.position_number,
                    employeeId: position.employee.id,
                });
            }
            const unavailable = (meta?.unavailable_periods || []).find((period) => {
                const unavailableStart = dayjs(period.start);
                const unavailableEnd = dayjs(period.end);
                if (!unavailableStart.isBefore(end) || !start.isBefore(unavailableEnd)) {
                    return false;
                }
                const isHtCarryover = String(period.reason_code || '').trim().toUpperCase() === 'HT'
                    && Boolean(roster.is_night_shift)
                    && shiftWindow.end.startOf('day').isAfter(shiftWindow.start.startOf('day'))
                    && shiftWindow.start.isBefore(unavailableStart)
                    && shiftDate < unavailableStart.format('YYYY-MM-DD');
                return !isHtCarryover;
            });
            if (unavailable) {
                issues.push({
                    code: 'EMPLOYEE_UNAVAILABLE',
                    severity: 'ERROR',
                    message: `${position.employee.name}在${operation.operation_name}执行期间不可用：${unavailable.label || '不可用'}`,
                    operationId: operation.operation_plan_id,
                    positionNumber: position.position_number,
                    employeeId: position.employee.id,
                });
            }
            if (operation.task_type !== 'FLEXIBLE') {
                const existing = assignmentsByEmployee.get(position.employee.id) || [];
                existing.forEach((other) => {
                    if (
                        other.start.isBefore(end)
                        && start.isBefore(other.end)
                        && !isV4EmployeeOverlapAllowed(
                            operation,
                            other.operation,
                            shareGroupComponentByOperation,
                            enableUniqueEmployee,
                        )
                    ) {
                        issues.push({
                            code: 'OPERATION_OVERLAP',
                            severity: 'WARNING',
                            message: `${position.employee!.name}的${operation.operation_name}与${other.operation.operation_name}时间冲突；人工换人允许保留，请确认业务风险`,
                            operationId: operation.operation_plan_id,
                            positionNumber: position.position_number,
                            employeeId: position.employee!.id,
                        });
                    }
                });
                existing.push({ operation, position, start, end });
                assignmentsByEmployee.set(position.employee.id, existing);
            }
        });
    });
    return issues;
};

export const buildV4ManualOverride = (
    result: V4ResultData,
): { payload: V4ManualOverride; issues: V4ValidationIssue[] } => {
    const issues = validateV4WorkingResult(result);
    const assignments: V4ManualOverride['assignments'] = [];
    result.operations.forEach((operation) => {
        operation.positions.forEach((position) => {
            if (position.status !== 'ASSIGNED' || !position.employee) return;
            let date = position.roster_date || '';
            const shiftDate = position.shift_date || null;
            let shiftId = Number(position.shift_id || 0);
            const shiftPlanId = position.shift_plan_id || null;
            let plannedStart = position.assignment_start || operation.planned_start;
            let plannedEnd = position.assignment_end || operation.planned_end;
            if (!date || !shiftId) {
                const context = resolveAssignmentRosterContext(
                    position.employee.id,
                    operation.operation_plan_id,
                    position.position_number,
                    operation,
                    result.shift_assignments,
                    result.assignments,
                );
                if (context) {
                    date = context.date;
                    shiftId = context.shift_id;
                }
            }
            assignments.push({
                operation_id: operation.operation_plan_id,
                position_number: position.position_number,
                employee_id: position.employee.id,
                is_standalone: operation.batch_code === 'STANDALONE',
                date,
                shift_date: shiftDate,
                shift_id: shiftId,
                shift_plan_id: shiftPlanId,
                planned_start: plannedStart,
                planned_end: plannedEnd,
            });
        });
    });
    return {
        payload: {
            assignments,
            shift_schedule: result.shift_assignments.map((shift) => ({
                employee_id: shift.employee_id,
                date: shift.date,
                shift_id: shift.shift_id,
            })),
        },
        issues,
    };
};

const coefficientBalance = (values: number[]): number => {
    if (values.length === 0) return 100;
    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    if (mean === 0) return 100;
    const variance = values.reduce((sum, value) => sum + ((value - mean) ** 2), 0) / values.length;
    return Math.max(0, Math.min(100, Math.round((1 - (Math.sqrt(variance) / mean)) * 100)));
};

export const selectV4Metrics = (result: V4ResultData) => {
    const positions = result.operations.flatMap((operation) => operation.positions);
    const assignedPositions = positions.filter((position) => position.status === 'ASSIGNED' && position.employee).length;
    const coveredOperations = result.operations.filter((operation) => (
        operation.positions.some((position) => position.status === 'ASSIGNED' && position.employee)
    )).length;
    const completedOperations = result.operations.filter((operation) => (
        operation.positions.length > 0
        && operation.positions.every((position) => position.status === 'ASSIGNED' && position.employee)
    )).length;
    const employeeIds = Array.from(new Set(result.shift_assignments.map((shift) => shift.employee_id)));
    const nonWorkDays = new Set(result.calendar_days.filter((day) => !day.is_workday).map((day) => day.date));
    const workHoursByEmployee = new Map(employeeIds.map((employeeId) => [employeeId, 0]));
    const nightsByEmployee = new Map(employeeIds.map((employeeId) => [employeeId, 0]));
    const weekendsByEmployee = new Map(employeeIds.map((employeeId) => [employeeId, 0]));
    result.shift_assignments.forEach((shift) => {
        if (shift.attendance_kind !== 'WORK') return;
        workHoursByEmployee.set(
            shift.employee_id,
            (workHoursByEmployee.get(shift.employee_id) || 0) + Number(shift.credited_hours || 0),
        );
        if (shift.is_night_shift) {
            nightsByEmployee.set(shift.employee_id, (nightsByEmployee.get(shift.employee_id) || 0) + 1);
        }
        if (nonWorkDays.has(shift.date)) {
            weekendsByEmployee.set(shift.employee_id, (weekendsByEmployee.get(shift.employee_id) || 0) + 1);
        }
    });
    const hoursBalance = coefficientBalance(Array.from(workHoursByEmployee.values()));
    const nightBalance = coefficientBalance(Array.from(nightsByEmployee.values()));
    const weekendBalance = coefficientBalance(Array.from(weekendsByEmployee.values()));
    const completionRate = positions.length > 0 ? Math.round((assignedPositions / positions.length) * 100) : 0;
    const coverageRate = result.operations.length > 0 ? Math.round((coveredOperations / result.operations.length) * 100) : 0;
    const qualityScore = Math.round(
        0.3 * completionRate
        + 0.2 * coverageRate
        + 0.2 * hoursBalance
        + 0.15 * nightBalance
        + 0.15 * weekendBalance,
    );
    return {
        completionRate,
        coverageRate,
        qualityScore,
        hoursBalance,
        nightBalance,
        weekendBalance,
        balanceIndex: Math.round((hoursBalance + nightBalance + weekendBalance) / 3),
        employeeCount: new Set(
            result.operations.flatMap((operation) => (
                operation.positions
                    .filter((position) => position.status === 'ASSIGNED' && position.employee)
                    .map((position) => position.employee!.id)
            )),
        ).size,
        totalPositions: positions.length,
        assignedPositions,
        totalOperations: result.operations.length,
        coveredOperations,
        completedOperations,
        uncoveredOperations: result.operations.filter((operation) => operation.status === 'UNASSIGNED').length,
        partialOperations: result.operations.filter((operation) => operation.status === 'PARTIAL').length,
    };
};
